import type { DragEndEvent } from '@dnd-kit/core';

import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import type { ScanPartContext, ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { getGalleryOperationToast } from '@/Client/Partials/FocusedPhotoGallery/GalleryPhotoManagement';
import {
  applyGalleryPhotoOperation,
  type GalleryPhotoOccurrence,
} from '@/Client/Partials/FocusedPhotoGallery/galleryPhotoOperations';
import { useConfirmationModal, useToast } from '@/Client/State';

type PhotoDragData = {
  type: 'photo';
  sources: readonly GalleryPhotoOccurrence[];
};

type NavigatorDropData =
  | { type: 'group'; groupId?: string }
  | { type: 'task'; groupId: string; taskId: string };

const getDropTargetLabel = (scanRoot: ScanRootOptimized, target: ScanPartContext): string => {
  const group = target.groupId ? scanRoot.groupsById[target.groupId] : undefined;
  const task = group && target.taskId ? group.tasksById[target.taskId] : undefined;
  if (task) return taskNamePresenter(task);
  if (group) return groupNamePresenter(group);
  return 'Curbside Photo';
};

/**
 * Dropping a photo thumbnail (dragged from any Focus Panel — see FocusedPhotoStrip.tsx
 * and FocusedPhotoGallery.tsx) onto a KaiNavigator group header or task row assigns it
 * there. Same group as every dragged photo's current assignment reads as "just add" per
 * the AC — a copy, so nothing already in that group is disturbed. A different group asks
 * Move or Copy, since either could be what the reader means.
 */
export const useKaiNavigatorPhotoDrop = () => {
  const { scanRoot, scanDataMutators } = useScanEditingContextSlice(['scanRoot', 'scanDataMutators']);
  const confirm = useConfirmationModal();
  const { showToast, showErrorToast } = useToast();

  return (event: DragEndEvent) => {
    const activeData = event.active.data.current as PhotoDragData | undefined;
    const overData = event.over?.data.current as NavigatorDropData | undefined;
    if (!scanRoot || activeData?.type !== 'photo' || !overData?.groupId) return;
    if (overData.type !== 'group' && overData.type !== 'task') return;

    const sources = activeData.sources;
    if (!sources.length) return;
    const target: ScanPartContext = overData.type === 'task'
      ? { groupId: overData.groupId, taskId: overData.taskId }
      : { groupId: overData.groupId };

    const apply = async (type: 'move' | 'copy') => {
      try {
        await scanDataMutators.setScan(current => applyGalleryPhotoOperation(current, { type, sources, target }));
        trackEvent(type === 'move' ? ANALYTICS_EVENTS.scanPhotosMoved : ANALYTICS_EVENTS.scanPhotosCopied, {
          scanId: scanRoot.id,
          photoCount: sources.length,
          source: 'navigator_drop',
        });
        showToast(getGalleryOperationToast(sources.length, type));
      } catch (error) {
        showErrorToast(error, `${type} photo assignment${sources.length === 1 ? '' : 's'}`);
      }
    };

    const sameGroup = sources.every(source => source.context.groupId === target.groupId);
    if (sameGroup) return apply('copy');

    const count = sources.length;
    const countLabel = count === 1 ? 'this photo' : `these ${count} photos`;
    confirm({
      title: `Move or copy ${count === 1 ? 'photo' : `${count} photos`}`,
      message: `Move or copy ${countLabel} into ${getDropTargetLabel(scanRoot, target)}?`,
      confirmLabel: 'Move',
      onConfirm: () => apply('move'),
      alternateLabel: 'Copy',
      onAlternate: () => apply('copy'),
    });
  };
};
